import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Ban, Plus, X, ShieldAlert, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * Audit Log — Restricted ZIP codes panel.
 *
 * Reads/writes `/api/admin/restricted-zips`. Super Admin adds or removes any
 * ZIP; other roles see a read-only chip list so they know which addresses
 * will be auto-held on order create.
 */
export default function RestrictedZipsPanel() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [rows, setRows] = useState([]); // [{zip, created_at, created_by_name}]
  const [notifyEmail, setNotifyEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [input, setInput] = useState("");
  const [filter, setFilter] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/restricted-zips");
      setRows(data?.rows || []);
      setNotifyEmail(data?.notify_email || "");
    } catch (e) {
      toast.error(`Could not load restricted ZIPs: ${formatApiError(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim();
    if (!q) return rows;
    return rows.filter((r) => (r.zip || "").includes(q));
  }, [rows, filter]);

  const addZip = async (e) => {
    e?.preventDefault?.();
    const raw = input.trim();
    if (!/^\d{5}$/.test(raw)) {
      toast.error("ZIP must be exactly 5 digits (e.g. 33131).");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/restricted-zips", { zip: raw });
      if (data?.created) {
        toast.success(`ZIP ${raw} added to the restricted list.`);
      } else {
        toast.info(`ZIP ${raw} was already in the list.`);
      }
      setInput("");
      inputRef.current?.focus();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeZip = async (zip) => {
    if (!window.confirm(`Remove ZIP ${zip} from the restricted list?\n\nExisting held orders keep their approval status — this only affects orders created AFTER now.`)) {
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/admin/restricted-zips/${zip}`);
      toast.success(`ZIP ${zip} removed from the restricted list.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 rounded-2xl border-border bg-card/80" data-testid="restricted-zips-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-destructive/10 text-destructive text-[10px] uppercase tracking-[0.16em] font-semibold mb-2">
            <ShieldAlert className="h-3 w-3" /> Restricted ZIPs
          </div>
          <h2 className="text-lg font-display font-semibold tracking-tight">Restricted ZIP codes</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Any new order whose delivery address falls in one of these ZIPs is automatically placed <strong>On Hold</strong> and requires SRCI Super Admin approval before it can be routed.{" "}
            {notifyEmail ? <>Notifications land at <code className="px-1 py-0.5 rounded bg-secondary text-foreground text-[11px]">{notifyEmail}</code>.</> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
          <span className="font-mono-num font-semibold text-foreground" data-testid="restricted-zips-count">{rows.length}</span>
          ZIPs configured
        </div>
      </div>

      {canEdit && (
        <form onSubmit={addZip} className="mt-4 flex flex-wrap gap-2 items-end" data-testid="restricted-zip-add-form">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Add a ZIP</label>
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
              placeholder="e.g. 33131"
              inputMode="numeric"
              maxLength={5}
              disabled={saving}
              data-testid="restricted-zip-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving || input.length !== 5}
            className="rounded-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground"
            data-testid="restricted-zip-add-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
            Add ZIP
          </Button>
        </form>
      )}

      <div className="mt-4 relative max-w-xs">
        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
          placeholder="Filter…"
          className="pl-8 h-9"
          data-testid="restricted-zip-filter"
        />
      </div>

      <div className="mt-4" data-testid="restricted-zip-list">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4" data-testid="restricted-zip-empty">
            {rows.length === 0
              ? "No ZIPs configured yet. Add one above to start holding orders for approval."
              : "No ZIPs match the filter."}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {filtered.map((r) => (
              <span
                key={r.zip}
                className="group inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border border-destructive/30 bg-destructive/5 text-destructive text-[13px] font-mono-num font-semibold"
                title={r.created_by_name ? `Added by ${r.created_by_name}` : undefined}
                data-testid={`restricted-zip-chip-${r.zip}`}
              >
                <Ban className="h-3 w-3 opacity-70" />
                {r.zip}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeZip(r.zip)}
                    className="ml-1 h-5 w-5 rounded-full grid place-items-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
                    aria-label={`Remove ZIP ${r.zip}`}
                    data-testid={`restricted-zip-remove-${r.zip}`}
                    disabled={saving}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {!canEdit && rows.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-3">
          Only Super Admin can add or remove ZIPs.
        </p>
      )}
    </Card>
  );
}
